import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'

import { formatDateTime } from '../utils/dateTime'
import { csrfToken } from '../utils/csrfToken'
import { initTooltips } from '../utils/tooltips'

function avatarTitle(user) {
  return user?.full_name || user?.email || ''
}

function NotificationRow({ notification, onClick }) {
  const actor = notification.actor
  const actorLabel = avatarTitle(actor)

  return (
    <button
      type="button"
      className={`px-3 list-group-item list-group-item-action text-start ${notification.read_at ? '' : 'bg-primary-subtle'}`}
      onClick={onClick}
    >
      <div className="row">
        <div className="col-auto">
          {actor ? (
            <span className="avatar avatar-sm" data-bs-toggle="tooltip" data-bs-title={actorLabel} aria-label={actorLabel}>
              <img src={actor.avatar_url} className="avatar-img rounded-circle" alt={actorLabel} />
            </span>
          ) : (
            <div className="avatar avatar-sm">
              <div className="avatar-title fs-lg bg-primary-soft rounded-circle text-primary">
                <i className="fe fe-bell"></i>
              </div>
            </div>
          )}
        </div>
        <div className="col ms-n2">
          <h5 className="mb-1">{notification.title}</h5>
          <small className="text-muted">{formatDateTime(notification.created_at)}</small>
        </div>
      </div>
    </button>
  )
}

function buildDailyReportUrl(dailyReportId) {
  const url = new URL('/users/daily_reports', window.location.origin)
  if (dailyReportId) url.searchParams.set('daily_report_id', dailyReportId)
  return `${url.pathname}${url.search}`
}

function NotificationsCenter() {
  const [tab, setTab] = useState('unread')
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const mounted = useRef(false)

  async function load() {
    const [listRes, countsRes] = await Promise.all([
      fetch('/notifications.json', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }),
      fetch('/notifications/counts.json', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }),
    ])

    if (listRes.ok) {
      const payload = await listRes.json()
      setNotifications(payload.notifications || [])
    }

    if (countsRes.ok) {
      const payload = await countsRes.json()
      setUnreadCount(payload.unread_count || 0)
    }
  }

  useEffect(() => {
    let cancelled = false
    let timer
    setLoading(true)

    load()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    timer = setInterval(() => {
      load().catch(() => {})
    }, 60 * 1000)

    return () => {
      cancelled = true
      if (timer) clearInterval(timer)
    }
  }, [])

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
      return
    }
    initTooltips(document)
  }, [notifications])

  const unread = useMemo(() => notifications.filter((n) => !n.read_at), [notifications])
  const visible = tab === 'unread' ? unread : notifications

  async function markAllRead() {
    await fetch('/notifications/read_all.json', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken() || '',
      },
      credentials: 'same-origin',
    })
    await load()
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <ul className="nav nav-tabs nav-tabs-sm modal-header-tabs">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${tab === 'unread' ? 'active' : ''}`}
              onClick={() => setTab('unread')}
            >
              Unread{' '}
              <span className="badge rounded-pill text-bg-secondary-subtle">{unreadCount}</span>
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${tab === 'all' ? 'active' : ''}`}
              onClick={() => setTab('all')}
            >
              All{' '}
              <span className="badge rounded-pill text-bg-secondary-subtle">{notifications.length}</span>
            </button>
          </li>
        </ul>

        <button type="button" className="btn btn-sm btn-white" disabled={!unreadCount} onClick={() => markAllRead().catch(() => {})}>
          Read all
        </button>
      </div>

      {loading ? <div className="text-body-secondary">Loading…</div> : null}

      {visible.length ? (
        <div className="list-group list-group-flush list-group-activity my-n3">
          {visible.map((n) => (
            <NotificationRow
              key={n.id}
              notification={n}
              onClick={() => {
                if (n.daily_report_id) window.location.href = buildDailyReportUrl(n.daily_report_id)
              }}
            />
          ))}
        </div>
      ) : (
        <div className="text-body-secondary">No notifications.</div>
      )}
    </div>
  )
}

function NotificationsBadge() {
  const [count, setCount] = useState(0)

  async function load() {
    const res = await fetch('/notifications/counts.json', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
    if (!res.ok) return
    const payload = await res.json()
    setCount(payload.unread_count || 0)
  }

  useEffect(() => {
    let timer
    load().catch(() => {})
    timer = setInterval(() => {
      load().catch(() => {})
    }, 60 * 1000)
    return () => {
      if (timer) clearInterval(timer)
    }
  }, [])

  if (!count) return null

  return <span className="badge rounded-pill bg-primary">{count}</span>
}

export function mountNotificationsCenter(el) {
  if (!el) return
  const root = createRoot(el)
  root.render(<NotificationsCenter />)
}

export function mountNotificationsBadge(el) {
  if (!el) return
  const root = createRoot(el)
  root.render(<NotificationsBadge />)
}
